//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DataSource from 'devextreme/data/data_source'
import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs, IMRConfirmationPopupOptions } from '../common/confirmation-popup2'

//#endregion

import { ColumnModel } from '../api/api.models'

import { Enums } from '../../models/common/enums'

import { GridService } from '../../services/grid'
import { DetailsResult, SystemService } from '../../services/system'
import { Api, LocalDictionaryFactory } from '../api/api.module'

import { EventsNames } from '../../helpers/events-names'
import { IUserSession } from '../../helpers/user-session'
import { LocalizationHelper, LogHelper } from '../../helpers'

import { BaseGridController } from '../common/common.base.grid.controller'
import { ScopeBaseGrid } from '../common/common.base.grid.controller'
import { TreeListController, TreeListOptions } from '../common/tree-list/tree-list.controller'
import { ButtonsOptions, GridTypes } from '../common/grid/grid.controller'
import { Activity } from '../../models/common'
import { OpDataTypeGroup } from '../../models/core'
import { DataProviderListResult } from '../../data-provider'
import { DataTypeGroupService } from '../../services/data-type-group'

interface DataTypeGroupsScope extends ScopeBaseGrid {
    mainTreeListOptions: TreeListOptions
    showGrid: boolean
    confirmationPopupOptions: IMRConfirmationPopupOptions
}

export class DataTypeGroupsController extends BaseGridController {
    private get TREE_LIST_NAME(): string { return 'DataTypeGoupsTreeList' }

    private grid: TreeListController<OpDataTypeGroup>
    private treeListId: number
    private columns: ColumnModel[]
    private confirmationPopup: IIMRConfirmationPopup

    constructor(
        public $scope: DataTypeGroupsScope,
        protected $state: StateService,
        protected $timeout: ng.ITimeoutService,
        private $q: ng.IQService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        protected systemService: SystemService,
        protected userSession: IUserSession,
        private dataTypeGroupService: DataTypeGroupService,
        private gridService: GridService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, null, referenceType)

        this.setEvents()
        this.setOptions()
        this.setConstants()

        this.drawGrid()
    }

    //#region Events

    private setEvents(): void {
        this.$scope.$on(EventsNames.FILTERS_CLEARED, () => {
            this.$state.params['ids'] = null

            this.getDataTypeGroupsWithTreeListRefresh()
        })

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.getDataTypeGroupsWithTreeListRefresh())

        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.onDeleted()
        })
    }

    private onTreeListInitialized(e: any, grid: TreeListController<OpDataTypeGroup>): void {
        this.treeListId = e.tableId
        this.dataGrid = e.component
        this.grid = grid
    }

    private onTreeListNodeInitialized(e: any): void {
        if (this.dataGrid != null) {
            e.root.children.forEach((child: any) => {
                this.dataGrid.collapseRow(child.key)
            })
        }
    }

    private onTreeListContentReady(e: any): void {

    }

    private onShowDetails(e: any): void {
        this.dataTypeGroupService
            .openDetails(this.grid.selectedItemId, this.grid.pageItemsIds)
            .then((result: DetailsResult) => {
                if (result.status === DetailsResult.Status.Deleted || result.status === DetailsResult.Status.Edited) {
                    this.grid.dxTree.refresh();
                }
            });
    }

    private onAdd(): void {
        this.dataTypeGroupService.openCreate()
    }

    private onEdit(): void {
        if (this.grid.selectedCount === 1) {
            this.dataTypeGroupService.openEdit(this.grid.selectedItemId)
        } else {
            //no bulk edit
        }
    }

    private onDelete(args: any): void {
        this.confirmationPopup.show()
    }

    private onDeleted(): void {
        this.getDataTypeGroupsWithTreeListRefresh()
    }

    private onConfirmDelete(): void {
        this.dataTypeGroupService
            .deleteDataTypeGroup(this.grid.selectedItemId)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
            .fail((error: any) => {
                LogHelper.error(DataTypeGroupService.LOG_PREFIX, error)
            })
    }

    //#endregion


    //#region Options

    private setOptions(): void {
        this.setConfirmationPopup()
    }

    private getButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DATA_TYPE_GROUP_LIST) ? "['" + Activity[Activity.DATA_TYPE_GROUP_LIST] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DATA_TYPE_GROUP_DEL) ? "['" + Activity[Activity.DATA_TYPE_GROUP_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DATA_TYPE_GROUP_EDIT) ? "['" + Activity[Activity.DATA_TYPE_GROUP_EDIT] + "']" : null,
            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.DATA_TYPE_GROUP_ADD) ? "['" + Activity[Activity.DATA_TYPE_GROUP_ADD] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,

            onShowClick: (e: any) => {
                this.onShowDetails(e)
            },

            onAddClick: () => this.onAdd(),
            onEditClick: (args: any) => this.onEdit(),
            onDeleteClick: (args: any) => this.onDelete(args),
            onRefreshClick: () => this.getDataTypeGroupsWithTreeListRefresh()
        }
    }

    //#region IMRConfirmationPopup

    private setConfirmationPopup(): void {
        this.$scope.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion


    //#region Setup

    private getDataTypeGroups(): JQueryPromise<DataProviderListResult<OpDataTypeGroup>> {
        this.$scope.showGrid = false

        return this.dataTypeGroupService
            .getDataTypeGroups(this.columns)
            .always(() => {
                this.$scope.showGrid = true
            })
    }

    private getDataTypeGroupsWithTreeListRefresh(): void {
        this.dataGrid.beginCustomLoading(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Loading))

        this.getDataTypeGroups()
            .then((result: DataProviderListResult<any>) => {
                this.$scope.$root.$broadcast(EventsNames.UPDATE_GRID_DATASOURCE + this.treeListId, new DataSource(result.objects))
                this.dataGrid.endCustomLoading()
            })
    }

    private drawGrid(): void {
        this.dataTypeGroupService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.columns = columns
                this.getDataTypeGroups()
                    .then((result: DataProviderListResult<OpDataTypeGroup>) => {
                        this.$scope.mainTreeListOptions = {
                            referenceId: Enums.ReferenceType.IdDataTypeGroup,
                            idReferenceType: Enums.ReferenceType.IdDataTypeGroup,
                            name: this.TREE_LIST_NAME,
                            type: GridTypes.MAIN,
                            keyExpr: DataTypeGroupService.ID_FIELD,
                            parentIdExpr: DataTypeGroupService.PARENT_ID_FIELD,
                            defaultColumnsDictionary: defaultColumns,
                            columnsDictionary: columns,
                            addCustomFilters: true,
                            dataSource: new DataSource(result.objects),

                            allowColumnReordering: !this.userSession
                                .getLoggedOperator()
                                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DataTypeGroupMainGrid),

                            onInitialized: (e: any, grid: TreeListController<OpDataTypeGroup>) => {
                                this.onTreeListInitialized(e, grid)
                            },

                            onNodesInitialized: (e: any) => {
                                this.onTreeListNodeInitialized(e)
                            },

                            onContentReady: (e: any) => {
                                this.onTreeListContentReady(e)
                            },

                            onRowDoubleClick: (e: any) => {
                                this.onShowDetails(e)
                            },

                            buttonsOptions: this.getButtonsOptions(),
                            localStorageVarForSavedEntity: "dataTypeGroupSaved",
                        }
                    })
            })
    }

    //#endregion 

    //#region Constants

    private setConstants(): void {
    }
    //#endregion

}

